// The Blueprint Library's rules (plan 4A), used by the server and the browser.

export const CATEGORIES = ['Weapons Primary', 'Weapons Sidearms', 'Armor Core', 'Armor Helmet', 'Armor Arms', 'Armor Legs', 'Ammo', 'Ship components', 'Other'] as const;
export type Category = (typeof CATEGORIES)[number];

const SHIP_COMPONENTS: readonly string[] = ['WeaponGun', 'PowerPlant', 'Cooler', 'Shield', 'Radar', 'QuantumDrive'];

/** R1: FleetCom's `categorizeBlueprint`, plus ship components. */
export function categoryOf(type: string, subtype: string): Category {
  if (type === 'WeaponPersonal' && (subtype === 'Medium' || subtype === 'Large')) return 'Weapons Primary';
  if (type === 'WeaponPersonal' && subtype === 'Small') return 'Weapons Sidearms';
  if (type === 'Char_Armor_Torso') return 'Armor Core';
  if (type === 'Char_Armor_Helmet') return 'Armor Helmet';
  if (type === 'Char_Armor_Arms') return 'Armor Arms';
  if (type === 'Char_Armor_Legs') return 'Armor Legs';
  if (type === 'WeaponAttachment' && (subtype === 'Magazine' || subtype === 'Battery')) return 'Ammo';
  if (SHIP_COMPONENTS.includes(type)) return 'Ship components';
  return 'Other';
}

const TYPE_LABELS: Readonly<Record<string, string>> = {
  WeaponPersonal: 'Weapon',
  Char_Armor_Torso: 'Armor Core',
  Char_Armor_Helmet: 'Armor Helmet',
  Char_Armor_Arms: 'Armor Arms',
  Char_Armor_Legs: 'Armor Legs',
  WeaponAttachment: 'Attachment',
};

/** FleetCom's `getTypeLabel`: `Weapon · Medium`. */
export function typeLabel(type: string, subtype: string): string {
  const base = TYPE_LABELS[type] ?? type;
  return subtype === '' || subtype === 'UNDEFINED' ? base : `${base} · ${subtype}`;
}

/** What the filters need of a blueprint. */
export interface Listed {
  name: string;
  materials: string;
  category: Category;
  owned: boolean;
  inWiki: boolean;
}

export type Show = 'all' | 'owned' | 'missing';

export interface Filters {
  q: string;
  category?: Category | undefined;
  show: Show;
}

/** Search by name or material, ignoring case, then the chips. A row gone from the wiki shows only if it's owned. */
export function matches(row: Listed, { q, category, show }: Filters): boolean {
  if (!row.inWiki && !row.owned) return false;
  if (category !== undefined && row.category !== category) return false;
  if (show === 'owned' && !row.owned) return false;
  if (show === 'missing' && (row.owned || !row.inWiki)) return false;
  const wanted = q.trim().toLowerCase();
  return wanted === '' || row.name.toLowerCase().includes(wanted) || row.materials.toLowerCase().includes(wanted);
}

export interface Tally {
  total: number;
  owned: number;
  missing: number;
  percent: number;
  ownedGone: number;
  categories: Record<Category, number>;
}

/** R4: what the stats and the chips show. Rows gone from the wiki count only in `ownedGone`. */
export function tally(rows: readonly Listed[]): Tally {
  const categories = Object.fromEntries(CATEGORIES.map((name) => [name, 0])) as Record<Category, number>;
  let total = 0;
  let owned = 0;
  let ownedGone = 0;
  for (const row of rows) {
    if (!row.inWiki) {
      if (row.owned) ownedGone += 1;
      continue;
    }
    total += 1;
    categories[row.category] += 1;
    if (row.owned) owned += 1;
  }
  return { total, owned, missing: total - owned, percent: total === 0 ? 0 : Math.round((owned / total) * 100), ownedGone, categories };
}

/** R2: the wiki leaves `output_name` blank on a few radars, and their `output.name` too. */
export function blueprintName(row: { outputName: string | null | undefined; itemName: string | null | undefined; key: string | null | undefined }): string {
  return row.outputName || row.itemName || row.key || 'Unnamed blueprint';
}

/** A catalogue row, as a sync compares it. */
export interface CatalogRow {
  uuid: string;
  name: string;
  type: string;
  subtype: string;
  materials: string;
  isDefault: boolean;
  gameVersion: string;
}

export type StoredRow = CatalogRow & { inWiki: boolean };

export type SyncPlan = { ok: false; reason: string } | { ok: true; add: CatalogRow[]; change: CatalogRow[]; gone: string[] };

const same = (a: CatalogRow, b: CatalogRow) => a.name === b.name && a.type === b.type && a.subtype === b.subtype && a.materials === b.materials && a.isDefault === b.isDefault && a.gameVersion === b.gameVersion;

/** R3: what a sync would do, or why it mustn't. A row that came back counts as changed. */
export function syncPlan(stored: readonly StoredRow[], fetched: readonly CatalogRow[], options: { total: number; maxDropPercent: number }): SyncPlan {
  if (fetched.length === 0) return { ok: false, reason: 'The wiki listed no blueprints' };
  if (fetched.length !== options.total) return { ok: false, reason: `The wiki says it has ${String(options.total)} blueprints, but gave ${String(fetched.length)}` };
  const incoming = new Map(fetched.map((row) => [row.uuid, row]));
  if (incoming.size !== fetched.length) return { ok: false, reason: 'The wiki listed a blueprint twice' };
  const listed = stored.filter((row) => row.inWiki).length;
  const floor = (listed * (100 - options.maxDropPercent)) / 100;
  if (fetched.length < floor) return { ok: false, reason: `The wiki gave ${String(fetched.length)} blueprints, down from ${String(listed)}, which is more than the ${String(options.maxDropPercent)}% a sync may drop` };
  const known = new Map(stored.map((row) => [row.uuid, row]));
  const add: CatalogRow[] = [];
  const change: CatalogRow[] = [];
  for (const row of fetched) {
    const was = known.get(row.uuid);
    if (was === undefined) add.push(row);
    else if (!was.inWiki || !same(was, row)) change.push(row);
  }
  return { ok: true, add, change, gone: stored.filter((row) => row.inWiki && !incoming.has(row.uuid)).map((row) => row.uuid) };
}

export interface ImportingRow {
  id: string;
  uuid: string;
  name: string;
  created: string;
}

export type ImportPlan = { create: (ImportingRow & { since: Date })[]; skipped: number; refused: { id: string; reason: string }[] };

const UUID = /^[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/;
const RECORD_ID = /^[a-z0-9]{15}$/;

/** R5: what importing these rows does. A uuid twice in one slice counts once. */
export function importPlan(rows: readonly ImportingRow[], inCatalog: ReadonlySet<string>, owned: ReadonlySet<string>): ImportPlan {
  const plan: ImportPlan = { create: [], skipped: 0, refused: [] };
  const seen = new Set(owned);
  for (const row of rows) {
    const since = new Date(row.created.replace(' ', 'T'));
    if (!RECORD_ID.test(row.id)) plan.refused.push({ id: row.id, reason: 'Its id isn’t a record id' });
    else if (!UUID.test(row.uuid)) plan.refused.push({ id: row.id, reason: 'It has no wiki uuid' });
    else if (Number.isNaN(since.getTime())) plan.refused.push({ id: row.id, reason: 'Its date can’t be read' });
    else if (seen.has(row.uuid)) plan.skipped += 1;
    else if (!inCatalog.has(row.uuid)) plan.refused.push({ id: row.id, reason: `${row.name} isn’t in the catalogue` });
    else {
      seen.add(row.uuid);
      plan.create.push({ ...row, since });
    }
  }
  return plan;
}
